import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface InboxPaperSummary {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  abstract: string | null;
  doi: string | null;
}

export interface InboxItem {
  id: string;
  paper_id: string;
  source_concept_id: string | null;
  source_concept_name: string | null;
  source_type: string;
  user_note: string | null;
  suggested_concept_id: string | null;
  created_at: string;
  processed_at: string | null;
  processed_action: string | null;
  paper: InboxPaperSummary;
}

export function useInbox() {
  return useQuery({
    queryKey: ["inbox"],
    queryFn: () => apiClient.get("api/inbox").json<InboxItem[]>(),
  });
}

export function useInboxCount() {
  return useQuery({
    queryKey: ["inbox-count"],
    queryFn: () => apiClient.get("api/inbox/count").json<{ count: number }>(),
    refetchInterval: 60_000, // Poll every minute for badge updates
  });
}

export function useInboxActions() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["inbox"] });
    queryClient.invalidateQueries({ queryKey: ["inbox-count"] });
    // Inbox actions (dismiss/assign/defer) update flagged_for_inbox on the
    // ConceptPaper, so the Expand page needs to refetch papers to stay in sync.
    queryClient.invalidateQueries({ queryKey: ["papers"] });
    queryClient.invalidateQueries({ queryKey: ["paper-keys"] });
  };

  const createItem = useMutation({
    mutationFn: (data: { paper_id: string; source_concept_id?: string; user_note?: string; suggested_concept_id?: string }) =>
      apiClient.post("api/inbox", { json: { ...data, source_type: "concept_flag" } }).json<InboxItem>(),
    onSettled: invalidate,
  });

  const assignItem = useMutation({
    mutationFn: (data: { itemId: string; concept_id: string; working_set_id?: string }) =>
      apiClient.post(`api/inbox/${data.itemId}/assign`, {
        json: { concept_id: data.concept_id, working_set_id: data.working_set_id },
      }).json<InboxItem>(),
    onMutate: async ({ itemId }) => {
      await queryClient.cancelQueries({ queryKey: ["inbox"] });
      queryClient.setQueryData<InboxItem[]>(["inbox"], (old) =>
        old ? old.filter((i) => i.id !== itemId) : old,
      );
    },
    onSettled: invalidate,
  });

  const dismissItem = useMutation({
    mutationFn: (itemId: string) =>
      apiClient.post(`api/inbox/${itemId}/dismiss`).json<InboxItem>(),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ["inbox"] });
      queryClient.setQueryData<InboxItem[]>(["inbox"], (old) =>
        old ? old.filter((i) => i.id !== itemId) : old,
      );
    },
    onSettled: invalidate,
  });

  const deferItem = useMutation({
    mutationFn: (itemId: string) =>
      apiClient.post(`api/inbox/${itemId}/defer`).json<InboxItem>(),
    onSettled: invalidate,
  });

  return { createItem, assignItem, dismissItem, deferItem };
}
